import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Lock, Brain, RefreshCw, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNowStrict } from "date-fns";

const IntelFeed = () => {
  const { isPremium } = useAuth();
  const [manualRefresh, setManualRefresh] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ["intel-feed", manualRefresh],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("intel-feed");
      if (error) throw error;
      return data as { analysis: string; eventCount?: number; generatedAt?: string; error?: string };
    },
    enabled: isPremium,
    staleTime: 600000, // 10 min
    refetchInterval: 1800000, // 30 min
    retry: 1,
  });

  const renderMarkdown = (text: string) => {
    return text.split("\n").map((line, i) => {
      if (line.startsWith("## ")) {
        return (
          <h3 key={i} className="mt-4 mb-2 font-mono text-xs font-bold uppercase tracking-wider text-primary">
            {line.replace("## ", "")}
          </h3>
        );
      }
      if (line.startsWith("- ")) {
        return (
          <li key={i} className="ml-4 text-sm text-foreground/90 leading-relaxed list-disc">
            {line.replace("- ", "")}
          </li>
        );
      }
      if (line.trim() === "") return <br key={i} />;
      // Highlight severity keywords
      const highlighted = line
        .replace(/\bCRITICAL\b/g, '<span class="text-red-400 font-bold">CRITICAL</span>')
        .replace(/\bHIGH\b/g, '<span class="text-orange-400 font-bold">HIGH</span>')
        .replace(/\bELEVATED\b/g, '<span class="text-yellow-400 font-bold">ELEVATED</span>')
        .replace(/\bMODERATE\b/g, '<span class="text-blue-400 font-bold">MODERATE</span>')
        .replace(/\bLOW\b/g, '<span class="text-green-400 font-bold">LOW</span>');
      return <p key={i} className="text-sm text-foreground/90 leading-relaxed" dangerouslySetInnerHTML={{ __html: highlighted }} />;
    });
  };

  return (
    <section id="intel-feed" className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="h-5 w-5 text-primary" />
            <h2 className="font-mono text-lg font-bold uppercase tracking-wider text-foreground">
              AI Intel Feed
            </h2>
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-mono font-bold text-primary uppercase">
              Premium
            </span>
          </div>

          {isPremium && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setManualRefresh((p) => p + 1)}
              disabled={isLoading}
            >
              <RefreshCw className={`h-3 w-3 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          )}
        </div>

        {!isPremium ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground mb-1">AI Intelligence Brief</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Get AI-generated intelligence summaries with situation assessments, escalation risks, and 24-48h outlook.
            </p>
            <Button size="sm" className="text-xs font-semibold">Unlock with Premium</Button>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card p-6">
            {isLoading ? (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Generating intelligence brief from latest events...
              </div>
            ) : error || data?.error ? (
              <div className="text-sm text-destructive">
                {data?.error || "Failed to generate intelligence brief. Try again."}
              </div>
            ) : data?.analysis ? (
              <>
                <div className="prose-sm">{renderMarkdown(data.analysis)}</div>
                {data.generatedAt && (
                  <div className="mt-4 flex items-center gap-2 border-t border-border pt-3 text-[10px] text-muted-foreground font-mono">
                    <Clock className="h-3 w-3" />
                    Generated {formatDistanceToNowStrict(new Date(data.generatedAt), { addSuffix: true })}
                    {data.eventCount && <span>• Analyzed {data.eventCount} events</span>}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

export default IntelFeed;
